/**
 * The `profiles` collection: one row per user, written by the ela app.
 *
 * ELA Lab is a read-only view of this record — it never creates or updates a
 * profile. Every field except the ids is optional and may be an empty string
 * rather than absent, which is how the ela app stores "not filled in", so the
 * parser normalises blanks to `null` and the UI shows "Not provided".
 */

export interface Profile {
  /** The Appwrite document id. In this collection it equals `userId`. */
  readonly id: string;
  readonly userId: string;
  readonly name: string | null;
  readonly email: string | null;
  readonly phoneNumber: string | null;
  /** As stored — a date string when set, otherwise `null`. */
  readonly birthDate: string | null;
  /** File id in the profile-images bucket. */
  readonly profileImageId: string | null;
  readonly createdAt: Date | null;
  readonly updatedAt: Date | null;
}

export interface ProfileRow {
  $id: string;
  $createdAt?: string;
  $updatedAt?: string;
  [key: string]: unknown;
}

/** Trims, and treats a blank string as absent. */
function text(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = (typeof value === 'string' ? value : String(value)).trim();
  return trimmed === '' ? null : trimmed;
}

function date(value: unknown): Date | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Returns `null` for a row that cannot be used, so the caller can degrade. */
export function profileFromRow(row: ProfileRow): Profile | null {
  try {
    const id = row.$id;
    if (typeof id !== 'string' || id === '') return null;

    return {
      id,
      // The sample rows use the user id as the document id, but the column is
      // authoritative — fall back to `$id` only when the column is missing.
      userId: text(row['user_id']) ?? id,
      name: text(row['name']),
      email: text(row['email']),
      phoneNumber: text(row['phone_number']),
      birthDate: text(row['birth_date']),
      profileImageId: text(row['profile_image_id']),
      createdAt: date(row.$createdAt),
      updatedAt: date(row.$updatedAt),
    };
  } catch (error) {
    console.warn(`Skipping malformed profile row ${String(row.$id)}:`, error);
    return null;
  }
}

export function hasProfileImage(profile: Profile | null | undefined): boolean {
  return profile?.profileImageId != null;
}

/**
 * The name to show for a user.
 *
 * The profile row is the ela app's own record and wins; the Appwrite account
 * name is the fallback, and the email is the last resort so the UI never
 * renders an empty identity.
 */
export function displayName(
  profile: Profile | null | undefined,
  accountName: string | null | undefined,
  accountEmail: string | null | undefined,
): string {
  return (
    text(profile?.name) ?? text(accountName) ?? text(accountEmail) ?? 'Your account'
  );
}

/** Two letters for the avatar fallback. */
export function initialsOf(source: string): string {
  const trimmed = source.trim();
  if (trimmed === '') return '?';
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`;
  return trimmed.slice(0, 2);
}

/** `YYYY-MM-DD`, optionally followed by a time — nothing looser. */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/;

/**
 * Formats `birth_date` for display.
 *
 * The column is free text in this collection, so only an unambiguous ISO date
 * is reformatted. `new Date()` must not be trusted to decide: it parses
 * "spring 1991" as 1 January 1991 without complaint, which would show the
 * user a birth date they never entered. Anything else is shown as stored.
 */
export function formatBirthDate(value: string | null): string | null {
  if (value === null) return null;

  const match = ISO_DATE.exec(value.trim());
  if (match === null) return value;

  const [, year, month, day] = match;
  // Constructed from the parts, in local time, so a date-only value cannot
  // shift a day backwards for users west of UTC.
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.getMonth() !== Number(month) - 1 ||
    parsed.getDate() !== Number(day)
  ) {
    // A real-looking but impossible date such as 2026-02-31.
    return value;
  }

  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}
